import React, { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as _ from "lodash";
import ReactJson from "react-json-view";

import {
  Enterprise,
  EnterpriseAppState,
  Events,
  EventScope,
  EventSourceType,
  EventOrigin,
  GroupedEventList
} from "@ctra/api";

import {
  Button,
  DeleteOutlined,
  ImpactTracking,
  Spin,
  EditOutlined,
  Modal,
  ExclamationCircleOutlined,
  Space
} from "@ctra/components";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { isDebugMode, isPending, Optional } from "@ctra/utils";
import { GAContext } from "@ctra/analytics";

import {
  GACategory,
  EventIcon,
  useEvent,
  useEventList,
  CreateEventURLParams,
  useCreateEventDialog
} from "@events";

import { Routes } from "@routes";
import { useUserList } from "@base";
import { useFarm } from "@farms";

const { Header } = ImpactTracking;
const { useModal } = Modal;

const {
  events: {
    entity: { ctaText, actions },
    list: {
      entry: {
        meta: { source },
        cta: {
          remove: { confirm, yes, no }
        }
      }
    }
  }
} = Content;

/**
 * Event page
 * @return {JSX.Element}
 * @constructor
 */
export const EventPage: FC = () => {
  const dispatch = useDispatch();
  const { event, getTitle, getStartMoment, getDescription, getComponents } = useEvent();
  const { Page } = getComponents();
  const { t } = useTranslation();
  const { farm, site } = useFarm();
  const [modal, contextHolder] = useModal();
  const { users } = useUserList();

  const {
    api: { open }
  } = useCreateEventDialog();

  const {
    meta: { hash }
  } = useEventList<GroupedEventList>();

  const {
    id: eventId,
    source: { type, id: sourceId }
  } = event;

  /**
   * Tell if the given event is being deleted
   */
  const isDeleting = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, Events.types.DELETE_EVENT, { primaryValue: eventId })
  );

  let extra: Optional<JSX.Element> = void 0;

  /**
   * Event actions
   * @type {JSX.Element}
   */
  const toolbar = (
    <Space>
      <Button
        type="text"
        icon={<EditOutlined />}
        onClick={() => {
          open(hash, farm?.id, site?.id, CreateEventURLParams.editEventKey);
        }}
      >
        {t<string>(actions.edit)}
      </Button>
      <Button
        type="text"
        icon={<DeleteOutlined />}
        onClick={() => {
          modal.confirm({
            onOk: () => {
              dispatch(Events.actions.deleteEvent.start(eventId, type, sourceId));
            },
            icon: <ExclamationCircleOutlined />,
            content: t<string>(confirm),
            okText: t<string>(yes),
            cancelText: t<string>(no)
          });
        }}
      >
        {t<string>(actions.delete)}
      </Button>
    </Space>
  );

  switch (type) {
    case EventSourceType.genericInsight:
      extra = [EventScope.herdGroup, EventScope.pen].includes(event.scope.type) ? (
        void 0
      ) : (
        <Button
          type="secondary"
          onClick={() => {
            const search = new URLSearchParams();
            search.append("genericInsightID", sourceId.toString());

            Enterprise.history.push({
              pathname: Routes.app.insights.index,
              search: search.toString()
            });
          }}
        >
          {t(ctaText(EventSourceType.genericInsight))}
        </Button>
      );
      break;
  }

  const userID = event.context?.userID;
  const user = _.isString(userID) ? _.get(users, [userID]) : void 0;
  const humanReadable = user ? _.trim(`${user.firstName} ${user.lastName}`) : null;

  const label = t<string>(source, {
    user: humanReadable,
    origin: userID ? EventOrigin.manual : EventOrigin.automated
  });

  return (
    <Spin spinning={isDeleting}>
      {contextHolder}
      <div id="eventDetailsPage" data-reason-of-existence="pdf-export">
        <Header
          title={getTitle()}
          description={getDescription()}
          date={getStartMoment()}
          icon={<EventIcon withCircle category={event.category} />}
          extra={extra}
          toolbar={event.context?.userID ? toolbar : void 0}
          eventOrigin={event.context?.userID ? EventOrigin.manual : EventOrigin.automated}
          eventSource={label}
        />
        {isDebugMode() ? (
          <div onClick={(e) => e.stopPropagation()}>
            <ReactJson
              collapsed
              style={{
                border: "1px dashed #ccc",
                padding: 16,
                margin: "16px 0 0 0",
                maxHeight: 300,
                overflow: "scroll"
              }}
              src={event}
            />
          </div>
        ) : null}
        <GAContext.Provider value={{ category: GACategory.eventPage }}>
          <Page />
        </GAContext.Provider>
      </div>
    </Spin>
  );
};
